import { useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ALL_PERSONAS, usePersona, type Persona } from '@/lib/personas'
import { apiClient, unwrap } from '@/lib/apiClient'
import { HAS_AUTH } from '@/lib/auth'
import { useCurrentUser } from '@/lib/hooks'
import { cn } from '@/lib/cn'

interface PersonaSwitcherProps {
  variant?: 'icon' | 'compact'
  alignment?: 'left' | 'right'
}

export function PersonaSwitcher({
  variant = 'icon',
  alignment = 'left',
}: PersonaSwitcherProps) {
  const { persona, personaId, setPersona } = usePersona()
  const { data: user } = useCurrentUser()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  // Sync persona from server on first user load (server is source of truth across devices)
  useEffect(() => {
    if (!user?.settings?.persona) return
    if (user.settings.persona !== personaId) {
      setPersona(user.settings.persona as Persona)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.settings?.persona])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [open])

  const persistMutation = useMutation({
    mutationFn: (id: Persona) =>
      unwrap(apiClient.patch('/settings', { persona: id })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
  })

  const handleSelect = (id: Persona) => {
    setPersona(id)
    setOpen(false)
    if (HAS_AUTH) persistMutation.mutate(id)
  }

  const TriggerIcon = persona.Icon

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2 transition-colors',
          variant === 'icon'
            ? 'w-10 h-10 justify-center rounded-lg hover:bg-jarvis-surface text-jarvis-blue'
            : 'px-3 py-1.5 rounded-lg bg-jarvis-surface-2 border border-jarvis-border hover:bg-jarvis-surface text-sm',
          open && 'bg-jarvis-surface',
        )}
        title={`Persona: ${persona.wordmark}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <TriggerIcon
          size={variant === 'icon' ? 22 : 16}
          strokeWidth={2.5}
          className={cn(persona.id === 'symbiote' && 'rotate-180')}
        />
        {variant === 'compact' && (
          <span className="font-medium tracking-widest">{persona.wordmark}</span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute top-full mt-2 w-72 z-50',
            'bg-jarvis-surface border border-jarvis-border rounded-xl shadow-2xl',
            'overflow-hidden',
            alignment === 'left' ? 'left-0' : 'right-0',
          )}
        >
          <div className="px-4 py-3 border-b border-jarvis-border">
            <div className="text-[10px] uppercase tracking-widest text-jarvis-muted">
              Choose your persona
            </div>
          </div>
          <div className="py-1">
            {ALL_PERSONAS.map((p) => {
              const Icon = p.Icon
              const isActive = p.id === personaId
              return (
                <button
                  key={p.id}
                  onClick={() => handleSelect(p.id)}
                  className={cn(
                    'w-full px-4 py-3 flex items-start gap-3 text-left',
                    'hover:bg-jarvis-surface-2 transition-colors',
                    isActive && 'bg-jarvis-surface-2',
                  )}
                >
                  <span
                    className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center"
                    style={{
                      background: `${p.swatch.primary}20`,
                      color: p.swatch.primary,
                    }}
                  >
                    <Icon
                      size={16}
                      strokeWidth={2.5}
                      className={cn(p.id === 'symbiote' && 'rotate-180')}
                    />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold tracking-widest text-sm">
                        {p.wordmark}
                      </span>
                      {isActive && <Check size={14} className="text-jarvis-blue" />}
                    </div>
                    <div className="text-xs text-jarvis-muted">{p.tagline}</div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
